import z from "../zod";

export const COMMA_SEPARATED_ARRAY_PARAMETER = {
  style: "form",
  explode: false,
} as const;

export const DEEP_OBJECT_PARAMETER = {
  style: "deepObject",
  explode: true,
} as const;
export const QueryBooleanSchema = z.enum(["true", "false"]).transform((value) => value === "true");

export type UploadedFile = {
  fieldname: string;
  originalname: string;
  mimetype: string;
  size: number;
  buffer: Uint8Array;
};

const isUploadedFile = (val: unknown): val is UploadedFile => {
  if (!val || typeof val !== "object") return false;
  const f = val as Partial<UploadedFile>;
  return (
    typeof f.originalname === "string" &&
    typeof f.mimetype === "string" &&
    typeof f.size === "number" &&
    f.size > 0 &&
    f.buffer instanceof Uint8Array
  );
};

export const FileSchema = z
  .custom<UploadedFile>(isUploadedFile, {
    message: "A valid file upload is required",
  })
  .openapi({
    type: "string",
    format: "binary",
    description:
      "The binary file to upload. Supported formats and size limits depend on the operation.",
  });

export const IdSchema = z
  .string()
  .regex(/^[0-9a-f]{24}$/)
  .meta({
    title: "ID",
    description: "The unique identifier of the resource",
  });

export const isValidDateString = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};
export const PageQuerySchema = z.coerce.number().int().min(1).optional().meta({
  title: "Page",
  description: "Page number",
});

export const pageSizeQuerySchema = (
  max = 100,
  description = "Items per page",
) =>
  z.coerce.number().int().min(1).max(max).optional().meta({
    title: "Page Size",
    description,
  });

export const PageSizeQuerySchema = pageSizeQuerySchema();

export const DateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isValidDateString, "Must be a valid date in YYYY-MM-DD format")
  .meta({
    title: "Date",
    description: "A date string in the format YYYY-MM-DD",
  });

export const DateTimeStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/)
  .meta({
    title: "Date time",
    description:
      "A date-time string in the format YYYY-MM-DDTHH:mm:ss (optionally with milliseconds and timezone offset)",
  });

export const RelativeDateAnchorQueryEnum = z
  .enum(["today", "startOfWeek", "startOfMonth"])
  .meta({
    title: "Relative Date Anchor",
    description:
      "The current calendar point from which a relative date boundary is calculated.",
  });

export const RelativeDateUnitQueryEnum = z
  .enum(["days", "weeks", "months"])
  .meta({
    title: "Relative Date Unit",
    description: "The unit used to offset a relative date boundary.",
  });

export const RelativeDateBoundaryQuerySchema = z
  .object({
    anchor: RelativeDateAnchorQueryEnum,
    offset: z.coerce.number().int().min(-120).max(120).meta({
      title: "Offset",
      description:
        "The number of units before or after the selected calendar anchor.",
    }),
    unit: RelativeDateUnitQueryEnum,
  })
  .meta({
    title: "Relative Date Boundary",
    description:
      "A date boundary calculated from the current day, week, or month when the query runs.",
  });

export const RelativeDateRangeQuerySchema = z
  .object({
    from: RelativeDateBoundaryQuerySchema,
    to: RelativeDateBoundaryQuerySchema,
  })
  .meta({
    title: "Relative Date Range",
    description:
      "Date boundaries recalculated from the current day, week, or month whenever the query runs.",
  });

export const DateRangeQuerySchema = z
  .object({
    from: z.coerce.date().optional().meta({
      title: "From",
      description: "The inclusive absolute start of the date range.",
    }),
    to: z.coerce.date().optional().meta({
      title: "To",
      description: "The inclusive absolute end of the date range.",
    }),
    relative: RelativeDateRangeQuerySchema.optional().meta({
      title: "Relative Range",
      description:
        "Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.",
    }),
  })
  .meta({
    title: "Date Range Query",
    description:
      "An absolute or relative date range. Relative boundaries take precedence when supplied.",
  });

export const HexColorSchema = z
  .string()
  .regex(
    /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
    "Please enter a valid hex color (e.g. #1A2B3C or #ABC)",
  )
  .meta({
    title: "Hex Color",
    description: "A color in #RGB or #RRGGBB format",
  });

export const IntegrationProviderSchema = z
  .enum(["cloudcall", "dialpad", "devyce"])
  .meta({
    title: "Provider",
  });

export const VoipIntegrationProviderSchema = IntegrationProviderSchema.extract([
  "cloudcall",
  "dialpad",
  "devyce",
]);
